import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  TextField,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle"; 
import CancelIcon from "@mui/icons-material/Cancel"; 
import { useApiContext } from "../../../Utils/ApiContext/ApiContext";
import { getDecryptedAccessToken } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import ApiServices from "../../../Utils/ApiServices/ApiServices";

const AssignForms = ({ data,onFormAssigned ,appointmentData }) => {
  const { services } = useApiContext();
  const params = useParams();
  const pid = params.pid || appointmentData?.pc_pid;
  const eid = params.eid || appointmentData?.pc_eid;
  const accessToken = getDecryptedAccessToken();

  // State for search input
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingRow, setLoadingRow] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Check if the form is already assigned
  const isAlreadyAssigned = (serviceNo) => {
    return data?.some((item) => item.form_id === serviceNo);
  };

  // Remove duplicate services based on `service_no`
  const uniqueServices = services.reduce((acc, service) => {
    if (!acc.some((item) => item.service_no === service.service_no)) {
      acc.push(service);
    }
    return acc;
  }, []);

  // Sort: Assigned services first
  const sortedServices = [...uniqueServices].sort((a, b) => {
    const aAssigned = isAlreadyAssigned(a.service_no);
    const bAssigned = isAlreadyAssigned(b.service_no);
    return bAssigned - aAssigned; // Moves assigned services to the top
  });

  // Filter services based on search query
  const filteredServices = sortedServices.filter(
    (service) =>
      service.service.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.service_no.toString().includes(searchQuery)
  );

  // Function to call API for assigning forms
  const handleAssignForm = async (serviceNo) => {
    setLoadingRow(serviceNo);

    const postData = {
      pid: pid,
      eid: eid,
      form_id: serviceNo,
    };

    try {
      await ApiServices.post("/apis/default/api/sendFormsPatient", postData, {
        Authorization: `Bearer ${accessToken}`,
      });
      onFormAssigned ()
      setSnackbar({
        open: true,
        message: "Form assigned successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error assigning form. Try again.",
        severity: "error",
      });
      console.error("Error assigning form:", error);
    } finally {
      setLoadingRow(null);
    }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      {/* Search Field */}
      <Box mb={2}>
        <TextField
          fullWidth
          label="Search by Service Name or Service No"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      <TableContainer>
        <Table>
          {/* Table Header */}
          <TableHead sx={{ position: "sticky", top: 0, backgroundColor: "#f5f5f5", zIndex: 1 }}>
            <TableRow>
              <TableCell><b>Service No</b></TableCell>
              <TableCell><b>Service</b></TableCell>
              <TableCell><b>Pre-Treatment</b></TableCell>
              <TableCell><b>Consent Form</b></TableCell>
              <TableCell><b>Post-Treatment</b></TableCell>
              <TableCell><b>Treatment Form</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {filteredServices.map((service) => {
              const alreadyAssigned = isAlreadyAssigned(service.service_no);

              return (
                <TableRow key={service.id}>
                  <TableCell>{service.service_no}</TableCell>
                  <TableCell>{service.service}</TableCell>

                  {/* Pre-Treatment */}
                  <TableCell>
                    {service.has_pre_treatment === "1" ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </TableCell>

                  {/* Consent Form */}
                  <TableCell>
                    {service.has_consent_treatment === "1" ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </TableCell>

                  {/* Post-Treatment */}
                  <TableCell>
                    {service.has_post_treatment === "1" ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </TableCell>

                  {/* Treatment Form */}
                  <TableCell>
                    {service.has_treatment_form === "1" ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </TableCell>

                  {/* Assign Form Button / Already Assigned */}
                  <TableCell>
                    {alreadyAssigned ? (
                      <Chip
                        label="Already Assigned"
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={loadingRow === service.service_no}
                        onClick={() => handleAssignForm(service.service_no)}
                      >
                        {loadingRow === service.service_no ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          "Assign Form"
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Snackbar for Success/Error Messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AssignForms;
